import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';
import { SettingsProvider } from './hooks/useSettings';
import ErrorBoundary from './components/ErrorBoundary';
import { installGlobalErrorHandlers } from './utils/errorReporter';

// Sensible caching defaults so lists (products, search, shop-all) are NOT
// refetched on every navigation, scroll, or window focus. Data stays "fresh"
// for 5 minutes and is kept in cache for 30, so revisiting a page is instant.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,       // 5 min — no refetch while fresh
      gcTime: 30 * 60 * 1000,         // keep cached data 30 min
      refetchOnWindowFocus: false,    // don't refetch when the tab regains focus
      refetchOnReconnect: false,
      retry: 1,
    },
  },
});

// Capture uncaught errors & unhandled promise rejections site-wide.
installGlobalErrorHandlers();

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <SettingsProvider>
            <App />
          </SettingsProvider>
        </QueryClientProvider>
      </HelmetProvider>
    </ErrorBoundary>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();

// Unregister any old service workers so browsers always get fresh JS
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then(regs => {
    regs.forEach(reg => reg.unregister());
  });
}
